import { useEffect, useContext, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useGetData } from "../../../../../hooks/useGetData";
import { useMutationData } from "../../../../../hooks/useMutationData";
import { EvaluadoInterface } from "../../../../../interfaces/EvaluadoInterface";
import { FechasInterface } from "../../../../../interfaces/FechasInterface";
import { Loading } from "../../../../../components/Loading/Loading";
import { Button, Checkbox, Label, Textarea, TextInput } from "flowbite-react";
import SkyClear from "../../../../../img/SkyClear.png";
import { ThemeContext } from "../../../../../components/ThemeContext/ThemeContext";
import Header from "../../../../../components/HeaderForms/HeaderForms";

export const BienesCrearScreen = () => {
  const tituloFormulario = "BIENES INMUEBLES / MUEBLES";
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [sexo, setSexo] = useState("");
  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const { control, reset, handleSubmit } = useForm();
  const { isDarkMode } = useContext(ThemeContext);
  const [otroSexo, setOtroSexo] = useState("");
  const [edad, setEdad] = useState<number | null>(null);
  const [fechaNacimiento, setFechaNacimiento] = useState<string>("");
  const [formData, setFormData] = useState({});

  const mutateFecha = useMutationData({
    url: `/evaluado/${evaluadosList_id}/fecha-visita-bienesinmuebles`,
    reload: ["evaluado", `${evaluadosList_id}`, "fecha-visita-bienesinmuebles"],
  });

  const queryFecha = useGetData<FechasInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ["evaluado", "fecha-visita-bienesinmuebles"],
  });

  const [autos, setAutos] = useState<
    {
      marca: string;
      modelo: string;
      precioEstimado: string;
      liquidadoA: string;
    }[]
  >([{ marca: "", modelo: "", precioEstimado: "", liquidadoA: "" }]);

  const [bienesRaices, setBienesRaices] = useState<
    {
      tipo: string;
      ubicacion: string;
      precioEstimado: string;
      liquidadoA: string;
    }[]
  >([{ tipo: "", ubicacion: "", precioEstimado: "", liquidadoA: "" }]);

  const generoMap: { [key: string]: string } = {
    M: "Masculino",
    F: "Femenino",
    O: "Otro",
  };

  const [situacionSeleccionada, setSituacionSeleccionada] = useState<
    string | null
  >(null);
  const [nivelSocioeconomicoSeleccionado, setNivelSocioeconomicoSeleccionado] =
    useState<string | null>(null);
  const [tipoZonaSeleccionado, setTipoZonaSeleccionado] = useState<
    string | null
  >(null);

  const calcularEdad = (fechaNacimiento: string) => {
    const fechaNac = new Date(fechaNacimiento);
    const hoy = new Date();
    let edadCalculada = hoy.getFullYear() - fechaNac.getFullYear();
    const mes = hoy.getMonth() - fechaNac.getMonth();

    if (mes < 0 || (mes === 0 && hoy.getDate() < fechaNac.getDate())) {
      edadCalculada--;
    }

    return edadCalculada;
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData((prevData) => ({ ...prevData, [name]: checked }));
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleFechaNacimientoChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nuevaFechaNacimiento = event.target.value;
    setFechaNacimiento(nuevaFechaNacimiento);

    const nuevaEdad = calcularEdad(nuevaFechaNacimiento);
    setEdad(nuevaEdad);
  };

  const handleSexoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSexo(value);
    if (value !== "Otro") {
      setOtroSexo("");
    }
  };

  const handleSituacionChange = (option: string) => {
    setSituacionSeleccionada(option);
  };

  const handleNivelSocioeconomicoChange = (option: string) => {
    setNivelSocioeconomicoSeleccionado(option);
  };

  const handleTipoZonaChange = (option: string) => {
    setTipoZonaSeleccionado(option);
  };

  const renderHeader = () => (
    <>
      <Header titulo={tituloFormulario} />
    </>
  );

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ["evaluado"],
  });

  const mutateAutomovilBienes = useMutationData({
    url: `/autobienes/${evaluadosList_id}`,
    reload: ["evaluado", "autobienes", `${evaluadosList_id}`],
  });

  const mutateBienesRaices = useMutationData({
    url: `/bienesraices/${evaluadosList_id}`,
    reload: ["evaluado", "bienesraices", `${evaluadosList_id}`],
  });

  const mutateNotasBienes = useMutationData({
    url: `/notas-bienes-inmuebles/${evaluadosList_id}`,
    reload: ["evaluado", "notasbienes", `${evaluadosList_id}`],
  });

  const mutateSituacionInmueble = useMutationData({
    url: `/situacion-inmueble/${evaluadosList_id}`,
    reload: ["evaluado", "situacioninmueble", `${evaluadosList_id}`],
  });

  const mutateZonaInmueble = useMutationData({
    url: `/zona-inmueble/${evaluadosList_id}`,
    reload: ["evaluado", "zonainmueble", `${evaluadosList_id}`],
  });

  const mutateTipoZona = useMutationData({
    url: `/tipo-zona/${evaluadosList_id}`,
    reload: ["evaluado", "tipozona", `${evaluadosList_id}`],
  });

  const generoTexto =
    generoMap[queryEvaluado.data?.genero as string] || "No especificado";

  useEffect(() => {
    console.log(queryEvaluado.data);
    if (queryEvaluado.data) {
      const fechaNac = queryEvaluado.data.fecha_nacimiento || "";
      setFechaNacimiento(fechaNac);

      const nuevaEdad = calcularEdad(fechaNac);
      setEdad(nuevaEdad);

      reset({
        nombre: queryEvaluado.data.nombre || "",
        apellido_paterno: queryEvaluado.data.apellido_paterno || "",
        apellido_materno: queryEvaluado.data.apellido_materno || "",
        rfc: queryEvaluado.data.rfc || "",
        genero: queryEvaluado.data.genero || "",
        estado_civil: queryEvaluado.data.estado_civil || "",
        escolaridad: queryEvaluado.data.escolaridad || "",
        fecha_nacimiento: queryEvaluado.data.fecha_nacimiento || "",
        curp: queryEvaluado.data.curp || "",
        empresa: queryEvaluado.data.empresa?.nombre || "",
      });
    }
  }, [queryEvaluado.data, reset]);

  useEffect(() => {
    if (queryFecha.data) {
      reset({
        fecha_bienesinmuebles: queryFecha.data.fecha_bienesinmuebles || "",
        evaluado_uuid: queryFecha.data.evaluado_uuid || "",
      });
    }
  }, [queryFecha.data, reset]);

  if (queryEvaluado.isLoading) {
    return <Loading />;
  }

  const renderPage1 = () => (
    <div className="space-y-4 p-4">
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-1 flex items-center">
          <Label htmlFor="fecha_bienesinmuebles" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"} mr-2`}>
            FECHA VISITA:
          </Label>
          <Controller
            name="fecha_bienesinmuebles"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextInput
                id="fecha_bienesinmuebles"
                type="date"
                placeholder="Seleccionar fecha"
                {...field}
                className={`w-32 ${isDarkMode ? "bg-gray-700 text-white" : "bg-white text-gray-700"}`}
              />
            )}
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="nombre" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>NOMBRE:</Label>
          <TextInput
            id="nombre"
            name="nombre"
            value={`${queryEvaluado.data?.nombre || ""} ${
              queryEvaluado.data?.apellido_paterno || ""
            } ${queryEvaluado.data?.apellido_materno || ""}`}
            onChange={handleInputChange}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"}`}
          />
        </div>
        <div className="col-span-1">
          <Label htmlFor="rfc" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>RFC:</Label>
          <TextInput
            id="rfc"
            name="rfc"
            onChange={handleInputChange}
            value={queryEvaluado.data?.rfc || ""}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"}`}
          />
        </div>
        <div className="col-span-1">
          <Label htmlFor="edad" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>EDAD:</Label>
          <TextInput
            id="edad"
            name="edad"
            value={edad !== null ? edad.toString() : ""}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"}`}
          />
        </div>
        <div className="col-span-1">
          <Label htmlFor="sexo" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>SEXO:</Label>
          <TextInput
            id="sexo"
            name="sexo"
            value={generoTexto}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"}`}
          />
        </div>
        <div className="col-span-1">
          <Label htmlFor="estadoCivil" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>ESTADO CIVIL:</Label>
          <TextInput
            id="estadoCivil"
            name="estadoCivil"
            onChange={handleInputChange}
            value={`${queryEvaluado.data?.estado_civil || ""}`}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"}`}
          />
        </div>
        <div className="col-span-1">
          <Label htmlFor="escolaridad" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>ESCOLARIDAD:</Label>
          <TextInput
            id="escolaridad"
            name="escolaridad"
            onChange={handleInputChange}
            value={`${queryEvaluado.data?.escolaridad || ""}`}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"}`}
          />
        </div>
        <div className="col-span-1 flex items-center">
          <Label htmlFor="fechaNacimiento" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"} mr-2`}>
            FECHA DE NACIMIENTO:
          </Label>
          <TextInput
            id="fechaNacimiento"
            name="fechaNacimiento"
            value={`${queryEvaluado.data?.fecha_nacimiento || ""}`}
            disabled={true}
            type="date"
            onChange={handleInputChange}
            className={`w-32 ${isDarkMode ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"}`}
          />
        </div>
        <div className="col-span-1">
          <Label htmlFor="curp" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>CURP:</Label>
          <TextInput
            id="curp"
            name="curp"
            onChange={handleInputChange}
            value={queryEvaluado.data?.curp || ""}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"}`}
          />
        </div>
        <div className="col-span-1">
          <Label htmlFor="empresa" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>EMPRESA:</Label>
          <TextInput
            id="empresa_id"
            name="empresa"
            value={queryEvaluado.data?.empresa_id?.nombre || ""}
            onChange={handleInputChange}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"}`}
          />
        </div>
      </div>
    </div>
  );

  const renderPage2 = () => (
    <div className="space-y-4 p-4">
      <h2 className={`font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}>BIENES INMUEBLES / MUEBLES</h2>
      {autos.map((auto, index) => (
        <div key={index} className={`p-4 mb-4 ${isDarkMode ? "bg-gray-700" : "bg-blue-200"}`}>
          <h3 className={`font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}>AUTOMÓVIL {index + 1}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor={`marca_autobienes${index + 1}`} className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>MARCA</Label>
              <Controller
                name={`marca_autobienes${index + 1}`}
                control={control}
                render={({ field }) => (
                  <div>
                    <TextInput id={`marca_autobienes${index + 1}`} {...field} className={isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-700"} />
                  </div>
                )}
              />
            </div>
            <div>
              <Label htmlFor={`modelo_autobienes${index + 1}`} className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>MODELO</Label>
              <Controller
                name={`modelo_autobienes${index + 1}`}
                control={control}
                render={({ field }) => (
                  <div>
                    <TextInput
                      id={`modelo_autobienes${index + 1}`}
                      {...field}
                      className={isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-700"}
                    />
                  </div>
                )}
              />
            </div>
            <div>
              <Label htmlFor={`precio_autobienes${index + 1}`} className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>
                PRECIO ESTIMADO
              </Label>
              <Controller
                name={`precio_autobienes${index + 1}`}
                control={control}
                render={({ field }) => (
                  <div>
                    <TextInput
                      id={`precio_autobienes${index + 1}`}
                      {...field}
                      className={isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-700"}
                    />
                  </div>
                )}
              />
            </div>
            <div>
              <Label htmlFor={`liquidado_nombre_autobienes${index + 1}`} className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>
                LIQUIDADO A NOMBRE DE / CREDITO
              </Label>
              <Controller
                name={`liquidado_nombre_autobienes${index + 1}`}
                control={control}
                render={({ field }) => (
                  <div>
                    <TextInput
                      id={`liquidado_nombre_autobienes${index + 1}`}
                      {...field}
                      className={isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-700"}
                    />
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      ))}
      {autos.length < 5 && (
        <button
          type="button"
          className={`mt-4 rounded px-4 py-2 ${isDarkMode ? "bg-blue-700 text-white" : "bg-blue-500 text-white"}`}
          onClick={() =>
            setAutos([
              ...autos,
              { marca: "", modelo: "", precioEstimado: "", liquidadoA: "" },
            ])
          }
        >
          +
        </button>
      )}
    </div>
  );

  const renderPage3 = () => (
    <div className="space-y-4 p-4">
      <h2 className={`font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}>BIENES INMUEBLES / MUEBLES</h2>
      {bienesRaices.map((bien, index) => (
        <div key={index} className={`p-4 mb-4 ${isDarkMode ? "bg-gray-700" : "bg-blue-200"}`}>
          <h3 className={`font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}>BIENES RAÍCES {index + 1}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor={`tipo_bienes${index + 1}`} className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>TIPO</Label>
              <Controller
                name={`tipo_bienes${index + 1}`}
                control={control}
                render={({ field }) => (
                  <div>
                    <TextInput id={`tipo_bienes${index + 1}`} {...field} className={isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-700"} />
                  </div>
                )}
              />
            </div>
            <div>
              <Label htmlFor={`ubicacion_bienes${index + 1}`} className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>UBICACIÓN</Label>
              <Controller
                name={`ubicacion_bienes${index + 1}`}
                control={control}
                render={({ field }) => (
                  <div>
                    <TextInput id={`ubicacion_bienes${index + 1}`} {...field} className={isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-700"} />
                  </div>
                )}
              />
            </div>
            <div>
              <Label htmlFor={`precio_bienes${index + 1}`} className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>
                PRECIO ESTIMADO
              </Label>
              <Controller
                name={`precio_bienes${index + 1}`}
                control={control}
                render={({ field }) => (
                  <div>
                    <TextInput id={`precio_bienes${index + 1}`} {...field} className={isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-700"} />
                  </div>
                )}
              />
            </div>
            <div>
              <Label htmlFor={`liquidado_nombre_bienes${index + 1}`} className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>
                LIQUIDADO A NOMBRE DE / CREDITO
              </Label>
              <Controller
                name={`liquidado_nombre_bienes${index + 1}`}
                control={control}
                render={({ field }) => (
                  <div>
                    <TextInput
                      id={`liquidado_nombre_bienes${index + 1}`}
                      {...field}
                      className={isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-700"}
                    />
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      ))}
      {bienesRaices.length < 5 && (
        <button
          type="button"
          className={`mt-4 rounded px-4 py-2 ${isDarkMode ? "bg-blue-700 text-white" : "bg-blue-500 text-white"}`}
          onClick={() =>
            setBienesRaices([
              ...bienesRaices,
              { tipo: "", ubicacion: "", precioEstimado: "", liquidadoA: "" },
            ])
          }
        >
          +
        </button>
      )}
      <div>
        <Label htmlFor="notas" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>NOTAS:</Label>
        <Controller
          name="nota_bienes1"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <div>
              <Textarea
                id="nota_bienes1"
                {...field}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${target.scrollHeight}px`;
                }}
                style={{ height: "auto", overflow: "hidden" }}
                className={`p-3 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 w-full ${isDarkMode ? "bg-gray-800 text-white border-gray-600" : "bg-white text-gray-700 border-gray-300"}`}
                placeholder="Escribe aquí tus notas..."
              />
            </div>
          )}
        />
      </div>
    </div>
  );

  const renderPage4 = () => (
    <div className={`space-y-6 p-6 rounded-lg shadow-md ${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}>
      <div className="mb-4">
        <div className={`p-2 rounded-t-lg ${isDarkMode ? "bg-blue-900 text-white" : "bg-customBlue text-white"}`}>
          <h2 className="font-bold text-lg">SITUACIÓN DEL INMUEBLE</h2>
        </div>
        <div className={`grid grid-cols-3 gap-4 p-4 rounded-b-lg border-2 ${isDarkMode ? "border-blue-900 bg-gray-800" : "border-blue-600 bg-gray-100"}`}>
          {[
            "PROPIO",
            "RENTADO",
            "HIPOTECADO",
            "PRESTADO",
            "COMPARTIDO",
            "CASA SOLA",
            "DEPARTAMENTO",
            "CONDOMINIO",
            "CASA DUPLEX",
            "VECINDAD",
          ].map((option: string) => (
            <div key={option} className="flex items-center space-x-2">
              <Checkbox
                id={`situacion${option}`}
                checked={situacionSeleccionada === option}
                onChange={() => handleSituacionChange(option)}
                className={isDarkMode ? "bg-gray-600 text-blue-500 focus:ring-blue-500" : "text-blue-600 focus:ring-blue-600"}
              />
              <Label htmlFor={`situacion${option}`} className={`${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                {option}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <div className={`p-2 rounded-t-lg ${isDarkMode ? "bg-blue-900 text-white" : "bg-customBlue text-white"}`}>
          <h2 className="font-bold text-lg">ZONA</h2>
        </div>
        <div className={`grid grid-cols-3 gap-4 p-4 rounded-b-lg border-2 ${isDarkMode ? "border-blue-900 bg-gray-800" : "border-blue-600 bg-gray-100"}`}>
          {["ALTA", "MEDIA ALTA", "MEDIA", "MEDIA BAJA", "BAJA"].map(
            (option: string) => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`nivelSocioeconomico${option}`}
                  checked={nivelSocioeconomicoSeleccionado === option}
                  onChange={() => handleNivelSocioeconomicoChange(option)}
                  className={isDarkMode ? "bg-gray-600 text-blue-500 focus:ring-blue-500" : "text-blue-600 focus:ring-blue-600"}
                />
                <Label
                  htmlFor={`nivelSocioeconomico${option}`}
                  className={`${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  {option}
                </Label>
              </div>
            )
          )}
        </div>
      </div>

      <div className="mb-4">
        <div className={`p-2 rounded-t-lg ${isDarkMode ? "bg-blue-900 text-white" : "bg-customBlue text-white"}`}>
          <h2 className="font-bold text-lg">TIPO DE ZONA</h2>
        </div>
        <div className={`grid grid-cols-3 gap-4 p-4 rounded-b-lg border-2 ${isDarkMode ? "border-blue-900 bg-gray-800" : "border-blue-600 bg-gray-100"}`}>
          {["URBANA", "RURAL", "SUBURBANA"].map((option: string) => (
            <div key={option} className="flex items-center space-x-2">
              <Checkbox
                id={`tipoZona${option}`}
                checked={tipoZonaSeleccionado === option}
                onChange={() => handleTipoZonaChange(option)}
                className={isDarkMode ? "bg-gray-600 text-blue-500 focus:ring-blue-500" : "text-blue-600 focus:ring-blue-600"}
              />
              <Label htmlFor={`tipoZona${option}`} className={`${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                {option}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <Label
          htmlFor="notasAdicionales"
          className={`block font-bold ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
        >
          NOTAS:
        </Label>
        <Controller
          name="nota_bienes2"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <div>
              <Textarea
                id="nota_bienes2"
                {...field}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${target.scrollHeight}px`;
                }}
                style={{ height: "auto", overflow: "hidden" }}
                className={`mt-2 p-2 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 w-full ${isDarkMode ? "bg-gray-800 text-white border-gray-600" : "bg-white text-gray-700 border-gray-300"}`}
                placeholder="Escribe aquí tus notas..."
              />
            </div>
          )}
        />
      </div>

      <div className={`mt-4 p-4 border-t ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}>
        <div className="text-center">
          <p className={`text-left text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
            Bajo protesta de decir verdad, hago constar que los datos
            proporcionados son verdaderos.
          </p>
          <p className={`text-left text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
            Asimismo, autorizo a la empresa SKA TRACKING AND SECURITY S.A. de
            C.V. para que proporcione toda la Información, documentos y todos
            los datos que derive de las evaluaciones de pruebas de confianza que
            se me realicen.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
      <div className={`container mx-auto p-4`}>
        <form
          onSubmit={handleSubmit((data) => {
            const formData = {
              ...data,
              situacion_del_inmueble: situacionSeleccionada,
              zona_del_inmueble: nivelSocioeconomicoSeleccionado,
              tipo_de_zona: tipoZonaSeleccionado,
            };

            mutateFecha.mutate(
              {
                data: formData,
                tipo: "put",
              },
              {
                onSuccess: () => {
                  mutateAutomovilBienes.mutate(
                    {
                      data: formData,
                      tipo: "post",
                    },
                    {
                      onSuccess: () => {
                        mutateBienesRaices.mutate(
                          {
                            data: formData,
                            tipo: "post",
                          },
                          {
                            onSuccess: () => {
                              mutateNotasBienes.mutate(
                                {
                                  data: formData,
                                  tipo: "post",
                                },
                                {
                                  onSuccess: () => {
                                    mutateSituacionInmueble.mutate(
                                      {
                                        data: formData,
                                        tipo: "post",
                                      },
                                      {
                                        onSuccess: () => {
                                          mutateZonaInmueble.mutate(
                                            {
                                              data: formData,
                                              tipo: "post",
                                            },
                                            {
                                              onSuccess: () => {
                                                mutateTipoZona.mutate(
                                                  {
                                                    data: formData,
                                                    tipo: "post",
                                                  },
                                                  {
                                                    onSuccess: () => {
                                                      navigate(
                                                        `/domiciliaria/${evaluadosList_id}`
                                                      );
                                                    },
                                                    onError: (error) => {
                                                      console.error(
                                                        "Error al guardar Tipo de Zona:",
                                                        error
                                                      );
                                                    },
                                                  }
                                                );
                                              },
                                              onError: (error) => {
                                                console.error(
                                                  "Error al guardar Zona del Inmueble:",
                                                  error
                                                );
                                              },
                                            }
                                          );
                                        },
                                        onError: (error) => {
                                          console.error(
                                            "Error al guardar Situación del Inmueble:",
                                            error
                                          );
                                        },
                                      }
                                    );
                                  },
                                  onError: (error) => {
                                    console.error(
                                      "Error al guardar Notas de Bienes Inmuebles:",
                                      error
                                    );
                                  },
                                }
                              );
                            },
                            onError: (error) => {
                              console.error(
                                "Error al guardar Bienes Raíces:",
                                error
                              );
                            },
                          }
                        );
                      },
                      onError: (error) => {
                        console.error(
                          "Error al guardar Automóviles Bienes:",
                          error
                        );
                      },
                    }
                  );
                },
                onError: (error) => {
                  console.error("Error al guardar Fecha:", error);
                },
              }
            );
          })}
          className={`max-w-4xl mx-auto rounded-lg shadow-lg overflow-hidden ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          {renderHeader()}

          {currentPage === 1 && renderPage1()}
          {currentPage === 2 && renderPage2()}
          {currentPage === 3 && renderPage3()}
          {currentPage === 4 && renderPage4()}

          <div className="mt-6 p-4 flex justify-between">
            {currentPage < 2 && (
              <Button
                color="red"
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate(`/domiciliaria/${evaluadosList_id}`);
                }}
                className={isDarkMode ? "bg-red-600 hover:bg-red-700 text-white" : "bg-red-500 hover:bg-red-600 text-white"}
              >
                Cancelar
              </Button>
            )}
            {currentPage > 1 && (
              <button
                type="button"
                className={`rounded px-4 py-2 ${isDarkMode ? "bg-violet-600 text-white" : "bg-violet-300 text-gray-700"}`}
                onClick={() => {
                  window.scrollTo(0, 0);
                  handlePageChange(currentPage - 1);
                }}
              >
                Anterior
              </button>
            )}
            {currentPage < 4 && (
              <button
                type="button"
                className={`rounded px-4 py-2 ${isDarkMode ? "bg-blue-700 text-white" : "bg-blue-500 text-white"}`}
                onClick={() => {
                  handlePageChange(currentPage + 1);
                  window.scrollTo(0, 0);
                }}
              >
                Siguiente
              </button>
            )}
            {currentPage === 4 && (
              <div className="flex space-x-4">
                <button
                  type="submit"
                  className={`rounded px-4 py-2 ${isDarkMode ? "bg-green-600 text-white" : "bg-green-500 text-white"}`}
                >
                  Guardar
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};